import { useState, ReactNode } from 'react';
import * as Icons from 'lucide-react';
import { cn } from '../../lib/utils';

export interface HeaderAuthUser {
  id: string;
  email: string;
  name: string;
  tenant_id?: string;
  role?: string;
}

export interface HeaderProps {
  user?: HeaderAuthUser | null;
  onLogout?: () => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  className?: string;
  notificationSlot?: ReactNode;
  /** Slot rendered after the notification bell — intended for ThemeToggle. */
  themeSlot?: ReactNode;
}

export function Header({ 
  user, 
  onLogout,
  onProfileClick,
  onSettingsClick,
  searchPlaceholder = "Search...",
  showSearch = true,
  className,
  notificationSlot,
  themeSlot,
}: HeaderProps) {
  const [showUserMenu, setShowUserMenu] = useState(false);

  const getInitials = (name: string, email: string) => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'US';
  };

  const getRoleName = (role?: string) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'collaborator':
        return 'Collaborator';
      case 'agent':
        return 'Agent';
      default:
        return 'User';
    }
  };

  return (
    <header className={cn("h-14 border-b border-tf-border bg-tf-bg-card px-4 flex items-center justify-between shadow-sm", className)}>
      <div className="flex items-center gap-4">
        {/* Search */}
        {showSearch && (
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tf-text-sub" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              className="pl-10 pr-4 py-1.5 w-80 bg-tf-bg-hover border border-tf-border rounded-md text-sm text-tf-text placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-tf-ring focus:border-transparent transition-all"
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Notifications */}
        {notificationSlot || (
          <button className="relative p-2 rounded hover:bg-tf-bg-hover transition-all text-tf-icon hover:text-tf-icon-hover">
            <Icons.Bell className="w-5 h-5" />
          </button>
        )}

        {/* Theme */}
        {themeSlot}

        {/* Help */}
        <button className="p-2 rounded hover:bg-tf-bg-hover transition-all text-tf-icon hover:text-tf-icon-hover">
          <Icons.HelpCircle className="w-5 h-5" />
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-tf-border mx-1"></div>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-tf-bg-hover transition-all"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-tf-primary to-tf-interactive flex items-center justify-center">
              <span className="text-xs font-semibold text-white">
                {user ? getInitials(user.name, user.email) : 'US'}
              </span>
            </div>
            <span className="text-sm font-medium text-tf-text">
              {user?.name || 'Loading...'}
            </span>
            <Icons.ChevronDown className={cn("w-4 h-4 text-tf-icon transition-transform", showUserMenu && 'rotate-180')} />
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowUserMenu(false)}
              ></div>

              {/* Menu */}
              <div className="absolute right-0 mt-2 w-64 bg-tf-bg-card rounded-lg shadow-lg border border-tf-border z-20">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-tf-border">
                  <p className="text-sm font-medium text-tf-text">{user?.name || 'User'}</p>
                  <p className="text-xs text-tf-text-sub">{user?.email || ''}</p>
                  {user?.role && (
                    <p className="text-xs text-tf-text-sub mt-1">
                      {getRoleName(user.role)}
                    </p>
                  )}
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onProfileClick?.();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-tf-text hover:bg-tf-bg-hover transition-colors flex items-center gap-2"
                  >
                    <Icons.User className="w-4 h-4 text-tf-icon" />
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onSettingsClick?.();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-tf-text hover:bg-tf-bg-hover transition-colors flex items-center gap-2"
                  >
                    <Icons.Settings className="w-4 h-4 text-tf-icon" />
                    Settings
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-tf-text hover:bg-tf-bg-hover transition-colors flex items-center gap-2">
                    <Icons.Users className="w-4 h-4 text-tf-icon" />
                    Team Members
                  </button>
                </div>

                {/* Logout */}
                {onLogout && (
                  <div className="border-t border-tf-border py-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onLogout();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-tf-critical hover:bg-tf-critical-lighter transition-colors flex items-center gap-2"
                    >
                      <Icons.LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}













